import React, { PureComponent } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { withStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';
import CheckIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { green, red, blue } from '@mui/material/colors';
import MuiDialogTitle from '../DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';
import dataProvider from '../../providers/dataProvider';
import NumberFormatInput from '../NumberFormatInput/NumberFormatInput';
import DotsMenu from '../DotsMenu/DotsMenu';
import toFindFileInObjectByString from '../../utils/toFindFileInObjectByString';

// Constants for react-admin 3.x compatibility
const GET_ONE = 'GET_ONE';
const UPDATE = 'UPDATE';

// Video processing status constants
const VIDEO_STATUS = {
  IN_PROGRESS: 1,
  SUCCESS: 2,
  ERROR: 3,
};

const styles = (theme) => ({
  progressBar: {
    width: '12px !important',
    height: '12px !important',
    margin: '0 10px',
  },
  dialogTitle: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    margin: 0,
    padding: theme.spacing(2),
    marginBottom: '20px',
    textAlign: 'center',
  },
  dialogActions: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    display: 'inline-block',
  },
  addIcon: {
    cursor: 'pointer',
  },
  deleteIcon: {
    cursor: 'pointer',
    color: red[500],
  },
  inputTime: {
    width: '100px',
  },
  addRowButton: {
    margin: '20px auto 0px auto',
    display: 'flex',
  },
});

const Alert = ({ video }) => {
  if (!video) {
    return '';
  }

  const alerts = [];

  const iconStyles = (color) => ({
    marginRight: 5,
    verticalAlign: 'middle',
    color: color[500],
  });
  const textStyles = (color) => ({
    background: color[100],
    padding: 5,
    borderRadius: 3,
    marginBottom: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  });

  switch (video.transcodingStatus) {
    case VIDEO_STATUS.ERROR:
      alerts.push(
        <Typography style={{ ...textStyles(red) }}>
          <ErrorIcon style={{ ...iconStyles(red) }} />
          Error during video transcoding
        </Typography>,
      );
      break;
    case VIDEO_STATUS.SUCCESS:
      alerts.push(
        <Typography style={{ ...textStyles(green) }}>
          <CheckIcon style={{ ...iconStyles(green) }} />
          Video transcoding finished successfuly
        </Typography>,
      );
      break;
    default:
      alerts.push(
        <Typography style={{ ...textStyles(blue) }}>
          <InfoIcon style={{ ...iconStyles(blue) }} />
          Transcoding video...
        </Typography>,
      );
      break;
  }

  if (video.transcodingStatus !== VIDEO_STATUS.ERROR) {
    switch (video.transcribeStatus) {
      case VIDEO_STATUS.ERROR:
        alerts.push(
          <Typography style={{ ...textStyles(red) }}>
            <ErrorIcon style={{ ...iconStyles(red) }} />
            Error during generating subtitles
          </Typography>,
        );
        break;
      case VIDEO_STATUS.SUCCESS:
        alerts.push(
          <Typography style={{ ...textStyles(green) }}>
            <CheckIcon style={{ ...iconStyles(green) }} />
            Subtitles generated successfuly
          </Typography>,
        );
        break;
      default:
        alerts.push(
          <Typography style={{ ...textStyles(blue) }}>
            <InfoIcon style={{ ...iconStyles(blue) }} />
            Generating subtitles...
          </Typography>,
        );
        break;
    }
  }

  if (video.transcodedAt) {
    alerts.push(
      <Typography style={{ ...textStyles(green) }}>
        <InfoIcon style={{ ...iconStyles(green) }} />
        Video transcoded and updated at {new Date(video.transcodedAt).toLocaleString()}
      </Typography>,
    );
  }

  return alerts.map((alert, i) => <React.Fragment key={`key${i}`}>{alert}</React.Fragment>);
};

class UploadVideo extends PureComponent {
  emptyRow = { start: '', end: '', text: '' };

  constructor(props) {
    const { record, checkedName } = props;
    super(props);
    this.state = {
      open: false,
      loading: false,
      subtitles: [],
      video: toFindFileInObjectByString(record, checkedName),
    };
  }

  componentDidMount() {
    const { video } = this.state;
    this.setState(() => ({ loading: true }));

    if (!video) {
      return;
    }

    this.checkVideoStatus(video);

    if (video.transcribeStatus === VIDEO_STATUS.SUCCESS) {
      this.getSubtitles(video.id);
    }
  }

  handleClickOpen = () => {
    this.setState({
      open: true,
    });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  getSubtitles = (id) => {
    this.setState({ loading: true });
    return dataProvider('GET_ALL', `file/video/${id}/subtitles`)
      .then((res) => {
        if (typeof res.data === 'string') {
          this.setState({ loading: false });
          return;
        }
        this.setState({
          subtitles: res.data.subtitles,
          loading: false,
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  saveSubtitles = () => {
    const { video } = this.state;

    return dataProvider(UPDATE, `file/video/${video.id}/subtitles`, {
      id: '',
      data: { subtitles: this.state.subtitles },
    }).then((res) => {
      this.setState(() => ({
        subtitles: res.data.subtitles,
      }));
    });
  };

  checkVideoStatus = (video) => {
    const { transcodingStatus, transcribeStatus } = video;

    if (
      transcodingStatus === VIDEO_STATUS.ERROR ||
      transcodingStatus === VIDEO_STATUS.SUCCESS ||
      transcribeStatus === VIDEO_STATUS.ERROR ||
      transcribeStatus === VIDEO_STATUS.SUCCESS
    ) {
      this.setState({ loading: false });
    }
  };

  handleChange = (value, name, i) => {
    const subs = [...this.state.subtitles];
    subs[i][name] = value;
    this.setState(() => ({
      subtitles: subs,
    }));
  };

  removeAllNewlineCharacter = (subtitles) => {
    return subtitles.map((item) => {
      return (item.text = item.text.replace(/\n/, '#'));
    });
  };

  handleSave = () => {
    this.saveSubtitles();
    this.handleClose();
  };

  handleAction = (action, i) => {
    if (action && action.toLowerCase() === 'add row below') {
      this.insertRow(i);
    }
    if (action && action.toLowerCase() === 'delete') {
      this.deleteRow(i);
    }
  };

  insertRow = (index) => {
    const subtitles = [...this.state.subtitles];
    subtitles.splice(index + 1, 0, { start: '', end: '', text: '' });
    this.setState(() => ({ subtitles }));
  };

  deleteRow = (index) => {
    const subtitles = [...this.state.subtitles];
    if (subtitles.length > 1) {
      this.setState(() => ({
        subtitles: subtitles.filter((_, i) => i !== index),
      }));
    } else {
      this.setState(() => ({ subtitles: [{ ...this.emptyRow }] }));
    }
  };

  toValidAllList = () => {
    return this.state.subtitles.every((item) => {
      if (item.start && item.end && item.text) {
        return true;
      }
      return false;
    });
  };

  getButtonText = () => {
    const { loading, subtitles } = this.state;
    if (loading) {
      return 'Closed Captioning in Progress';
    }
    if (subtitles.length) {
      return 'Update Closed Captions';
    }
    return 'No Video Transcripts Are Detected';
  };

  renderRow = () => {
    const { classes } = this.props;
    return this.state.subtitles.map((item, i) => {
      return (
        <Grid container spacing={3} key={i}>
          <Grid size={{ md: 2 }}>
            <TextField
              className={classes.inputTime}
              error={!item.start}
              label="Start"
              required
              onChange={(event) => this.handleChange(event.target.value, 'start', i)}
              value={item.start}
              InputProps={{
                inputComponent: NumberFormatInput,
              }}
              margin="normal"
            />
          </Grid>
          <Grid size={{ md: 2 }}>
            <TextField
              label="End"
              className={classes.inputTime}
              error={!item.end}
              required
              onChange={(event) => this.handleChange(event.target.value, 'end', i)}
              value={item.end}
              InputProps={{
                inputComponent: NumberFormatInput,
                required: true,
              }}
              margin="normal"
            />
          </Grid>
          <Grid size={{ md: 7 }}>
            <TextField
              label="Text"
              error={!item.text}
              required
              fullWidth
              multiline
              value={item.text}
              onChange={(event) => this.handleChange(event.target.value, 'text', i)}
              margin="normal"
            />
          </Grid>
          <Grid size={{ md: 1 }}>
            <DotsMenu
              onChange={(e) => this.handleAction(e, i)}
              options={['Add row below', 'Delete']}
            />
          </Grid>
        </Grid>
      );
    });
  };

  render() {
    const { classes, record } = this.props;
    const { loading, video } = this.state;
    if (!video) {
      return null;
    }

    return (
      <>
        <Button
          size="small"
          color="primary"
          className={classes.button}
          disabled={loading}
          onClick={this.handleClickOpen}
        >
          {loading && <CircularProgress className={classes.progressBar} />}
          {this.getButtonText()}
        </Button>

        {!loading && <Alert video={video} />}

        <Dialog fullWidth maxWidth="md" onClose={this.handleClose} open={this.state.open}>
          <form className={classes.container}>
            <MuiDialogTitle className={classes.dialogTitle} onClose={this.handleClose}>
              Closed Captions
            </MuiDialogTitle>
            <DialogContent>
              <Typography variant="title">{record.name}</Typography>
              <p>
                <a target="_blank" href={video.url} rel="noreferrer">
                  {video.originalName}
                </a>
              </p>
              <br />
              <Grid container spacing={3}>
                <Grid size={{ md: 2 }}>Start time</Grid>
                <Grid size={{ md: 2 }}>End time</Grid>
                <Grid size={{ md: 7 }}>Closed Caption</Grid>
                <Grid size={{ md: 1 }} />
              </Grid>
              {this.renderRow()}
              {!this.state.subtitles.length ? (
                <Grid container spacing={3}>
                  <Grid size={{ md: 12 }}>
                    <Button
                      color="primary"
                      className={classes.addRowButton}
                      onClick={() => this.insertRow(-1)}
                    >
                      Add first row
                    </Button>
                  </Grid>
                </Grid>
              ) : null}
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button
                variant="contained"
                size="large"
                onClick={this.handleSave}
                color="primary"
                disabled={!this.toValidAllList()}
              >
                Save
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </>
    );
  }
}

export default withStyles(styles)(UploadVideo);
